import {
  type Amount,
  amountToString,
  maxAmount,
  minAmount,
  multiplyByRate,
  newAmount,
  newFinancialRate,
} from '@orus.eu/amount'
import { assertDefinedAndNotNull } from '@orus.eu/error'

import {
  type InsuranceLimitOfIndemnity,
  isInsuranceItemActive,
  type LimitOfIndemnityPart,
  type PossibleInsuranceLimitOfIndemnity,
} from '../../../model/policy-item.ts'
import {
  getFrMrpaxeApplicableAssetsValue,
  getFrMrpaxeBrokenMaterialLimit,
  getFrMrpaxeCashAndValuesLimit,
  getFrMrpaxeOptionBdgLimit,
  getFrMrpaxeOptionPeMonths,
  getFrMrpaxeOutdoorFurnitureLimit,
  getFrMrpaxePreservedGoodsRate,
  getFrMrpaxeRealEstateLimit,
  getFrMrpaxeStolenAssetsRate,
} from '../fr-mrpaxe-derogations.ts'
import type { FrMrpaxePolicyItem, FrMrpaxePolicyItemsState } from '../fr-mrpaxe-insurance.ts'

type FrMrpaxeLimitOfIndemnityEntry = { additionalInfo: string | null } & (
  | { limit: PossibleInsuranceLimitOfIndemnity; select?: never }
  | { select: (state: FrMrpaxePolicyItemsState) => InsuranceLimitOfIndemnity; limit?: never }
)

const FIVE_MILLION = newAmount(5_000_000)
/** The agreed terms cap the business interruption at 1.5 M€ a year when the annual revenue is higher. */
const PE_LIMIT_CAP = newAmount(1_500_000)

const premises: LimitOfIndemnityPart = {
  label: 'Plafond d’indemnisation de vos locaux',
  limit: { type: 'fixedAmount', value: FIVE_MILLION },
}

function contents(value: Amount): LimitOfIndemnityPart {
  return { label: 'Plafond d’indemnisation du contenu de vos locaux', limit: { type: 'calculatedAmount', value } }
}

function realEstate(state: FrMrpaxePolicyItemsState): LimitOfIndemnityPart {
  return {
    label: 'Plafond d’indemnisation des aménagements immobiliers intérieurs et extérieurs',
    limit: {
      type: 'calculatedAmount',
      value: getFrMrpaxeRealEstateLimit(state.frMrpaxeDerogationRealEstate ?? undefined),
    },
  }
}

function applicableAssetsValue(state: FrMrpaxePolicyItemsState): Amount {
  const assetsValue = assertDefinedAndNotNull(
    state.mrpAssetsValue,
    'The FR-MRPAXE limits of indemnity are computed from the assets value',
    {},
  )
  return getFrMrpaxeApplicableAssetsValue(assetsValue, state.frMrpaxeDerogationAssetsValue ?? undefined)
}

function estimatedRevenue(state: FrMrpaxePolicyItemsState): Amount {
  return assertDefinedAndNotNull(
    state.estimatedRevenue,
    'The FR-MRPAXE limits of indemnity are computed from the estimated revenue',
    {},
  )
}

function isBoostActive(state: FrMrpaxePolicyItemsState): boolean {
  return !!state.frMrpaxeBoostPolicyItem && isInsuranceItemActive(state.frMrpaxeBoostPolicyItem.optionality)
}

/**
 * The damage covers that insure the contents up to their declared value, the real estate fittings, and the premises up
 * to five million.
 */
function contentsRealEstateAndPremises(state: FrMrpaxePolicyItemsState): InsuranceLimitOfIndemnity {
  return { type: 'multipleLimits', limits: [contents(applicableAssetsValue(state)), realEstate(state), premises] }
}

/** Fire and terrorism share one row of the agreed terms, which adds the outdoor furniture. */
function fireAndTerrorism(state: FrMrpaxePolicyItemsState): InsuranceLimitOfIndemnity {
  return {
    type: 'multipleLimits',
    limits: [
      contents(applicableAssetsValue(state)),
      realEstate(state),
      {
        label: 'Plafond d’indemnisation du mobilier extérieur',
        limit: {
          type: 'calculatedAmount',
          value: getFrMrpaxeOutdoorFurnitureLimit(state.frMrpaxeDerogationOutdoorFurniture ?? undefined),
        },
      },
      premises,
    ],
  }
}

function stolenContentsAndPremises(state: FrMrpaxePolicyItemsState): LimitOfIndemnityPart[] {
  const rate = getFrMrpaxeStolenAssetsRate(state.frMrpaxeDerogationStolenAssetsValue ?? undefined)
  return [contents(multiplyByRate(applicableAssetsValue(state), rate)), premises]
}

/**
 * The limit of each policy item, as the quote page and the agreed terms show them. The underwriter's derogations
 * replace the matching default: contents value, stolen contents share, cash and values, broken material, preserved
 * goods, real estate fittings, outdoor furniture, and the PE and BDG limits. The covers keep the fittings and outdoor
 * furniture limits without BOOST: the BOOST entry lists the raised ones, with the 20,000 € fittings minimum of the
 * agreed terms.
 */
export const frMrpaxeLimitOfIndemnityByPolicyItem_v1_0_0 = {
  Incendie: { select: fireAndTerrorism, additionalInfo: null },
  DommagesElec: {
    select: (state) => {
      const assetsRelative = multiplyByRate(applicableAssetsValue(state), newFinancialRate(0.2))
      const contentsLimit = isBoostActive(state) ? maxAmount(assetsRelative, newAmount(20_000)) : assetsRelative
      return { type: 'multipleLimits', limits: [contents(contentsLimit), realEstate(state), premises] }
    },
    additionalInfo: null,
  },
  DegatsEaux: { select: contentsRealEstateAndPremises, additionalInfo: null },
  Vol: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        ...stolenContentsAndPremises(state),
        {
          label: 'Plafond d’indemnisation du vol de vos espèces, titres et valeurs',
          limit: {
            type: 'calculatedAmount',
            value: getFrMrpaxeCashAndValuesLimit(state.frMrpaxeDerogationCashAndValues ?? undefined),
          },
        },
      ],
    }),
    additionalInfo: 'Trois sinistres maximum par année d’assurance',
  },
  Vandalisme: {
    select: (state) => ({ type: 'multipleLimits', limits: stolenContentsAndPremises(state) }),
    additionalInfo: 'Trois sinistres maximum par année d’assurance',
  },
  EvenementsClim: { select: contentsRealEstateAndPremises, additionalInfo: null },
  CatastropheNat: { select: contentsRealEstateAndPremises, additionalInfo: null },
  Attentat: { select: fireAndTerrorism, additionalInfo: null },
  MarchConservee: {
    select: (state) => ({
      type: 'calculatedAmount',
      value: multiplyByRate(
        estimatedRevenue(state),
        getFrMrpaxePreservedGoodsRate(state.frMrpaxeDerogationPreservedGoods ?? undefined),
      ),
    }),
    additionalInfo: null,
  },
  BrisMateriel: {
    select: (state) => ({
      type: 'calculatedAmount',
      value: getFrMrpaxeBrokenMaterialLimit(state.frMrpaxeDerogationBrokenMaterial ?? undefined, isBoostActive(state)),
    }),
    additionalInfo: null,
  },
  RespCivOccupant: { limit: { type: 'fixedAmount', amount: FIVE_MILLION }, additionalInfo: null },
  publicLiability: { limit: { type: 'fixedAmount', amount: FIVE_MILLION }, additionalInfo: null },
  PE: {
    select: (state) => ({
      type: 'text',
      value: `${amountToString(minAmount(estimatedRevenue(state), PE_LIMIT_CAP), { addCurrency: true, displayDecimals: false })} sur ${getFrMrpaxeOptionPeMonths(state.frMrpaxeDerogationOptionPeValue ?? undefined)} mois max.`,
    }),
    additionalInfo: null,
  },
  BDG: {
    select: (state) => ({
      type: 'calculatedAmount',
      value: getFrMrpaxeOptionBdgLimit(state.frMrpaxeDerogationOptionBdgValue ?? undefined),
    }),
    additionalInfo: null,
  },
  PVV: {
    select: (state) => ({
      type: 'calculatedAmount',
      value: multiplyByRate(estimatedRevenue(state), newFinancialRate('1.5')),
    }),
    additionalInfo: null,
  },
  RCT: { limit: { type: 'fixedAmount', amount: FIVE_MILLION }, additionalInfo: null },
  BOOST: {
    select: (state) => ({
      type: 'multipleLimits',
      limits: [
        { label: 'Bris de matériel', limit: { type: 'text', value: 'Voir la garantie « Bris de matériel »' } },
        { label: 'Dommages électriques', limit: { type: 'text', value: 'Voir la garantie « Dommages électriques »' } },
        {
          label: 'Frais de réparation des conduites d’eau ayant causé le sinistre',
          limit: { type: 'fixedAmount', value: newAmount(500) },
        },
        {
          label: 'Mobilier extérieur',
          limit: {
            type: 'calculatedAmount',
            value: maxAmount(
              getFrMrpaxeOutdoorFurnitureLimit(state.frMrpaxeDerogationOutdoorFurniture ?? undefined),
              newAmount(5_000),
            ),
          },
        },
        {
          label: 'Aménagements immobiliers intérieurs et extérieurs',
          limit: {
            type: 'calculatedAmount',
            value: maxAmount(
              multiplyByRate(applicableAssetsValue(state), newFinancialRate(0.2)),
              getFrMrpaxeRealEstateLimit(state.frMrpaxeDerogationRealEstate ?? undefined),
            ),
          },
        },
      ],
    }),
    additionalInfo: 'Plafonds par sinistre',
  },
  assistance: { limit: { type: 'text', value: 'Voir convention d’assistance' }, additionalInfo: null },
} as const satisfies Record<FrMrpaxePolicyItem, FrMrpaxeLimitOfIndemnityEntry>
